import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { musicVideoEditorPath } from '../../../musicVideoEditor/musicVideoEditorPath';
import { useOpenMusicVideoEditor } from '../addedAssets/useOpenMusicVideoEditor';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';

/**
 * Studio → Full video → Music video opens the music video editor of the song (spec 2026-10-09
 * D11): the card has no step 3. An old link with `?theme=musicVideo` goes to the editor too; it
 * replaces the Studio page, so Back does not come here and redirect again. "Back" of the editor
 * returns to the asset types of the song.
 */
export function useStudioMusicVideoEntry(input: {
  accountId: number;
  catalogItemUuid: string;
  themeId: StudioThemeId | undefined;
  onPick: (themeId: StudioThemeId) => void;
}): (themeId: StudioThemeId) => void {
  const { accountId, catalogItemUuid, themeId, onPick } = input;
  const openEditor = useOpenMusicVideoEditor(accountId);
  const navigate = useNavigate();

  useEffect(() => {
    if (themeId === 'musicVideo') {
      const from = `/${SMLRouteNames.promote}/${SMLRouteNames.promoteStudio}?catalogItemUuid=${encodeURIComponent(
        catalogItemUuid
      )}`;
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(musicVideoEditorPath(catalogItemUuid, { accountId, from }), { replace: true });
    }
  }, [accountId, catalogItemUuid, navigate, themeId]);

  return useCallback(
    (next: StudioThemeId) => {
      if (next === 'musicVideo') {
        openEditor(catalogItemUuid);
        return;
      }
      onPick(next);
    },
    [catalogItemUuid, onPick, openEditor]
  );
}
